import React, { useState } from 'react'

import CloseSidebarButton from 'components/ui/CloseSidebarButton'
import OpenSidebarButton from 'components/ui/OpenSidebarButton'
import { PressableToggleButtonGroup } from 'components/ui/PressableToggleButtonGroup'
import type { BreakdownControlProps } from 'features/charts-v2/components/BreakdownControl'
import type { ChartTypePickerSectionSpec } from 'features/charts-v2/components/ChartTypePicker/ChartTypePicker'
import type { ChartTypeSelectorSelectionOverride } from 'features/charts-v2/components/ChartTypeSelector'
import { ChartTypeSelector } from 'features/charts-v2/components/ChartTypeSelector'
import { assertSectionsSatisfied } from 'features/charts-v2/components/ControlsSidebar/assert-sections'
import {
  ChartSectionWrapper,
  CollapsedShell,
  FilterHeaderActionsSlot,
  HeaderActions,
  HeaderRow,
  HeaderTitle,
  HeaderTitleGroup,
  PANEL_SWITCHER_COLORS,
  PanelSwitcherRow,
  ScrollArea,
  SectionsColumn,
  Shell,
} from 'features/charts-v2/components/ControlsSidebar/control-sidebar.styles'
import type { FilterControlProps } from 'features/charts-v2/components/FilterControl'
import type { MetricControlProps } from 'features/charts-v2/components/MetricControl'
import { SidebarSections } from 'features/charts-v2/components/SidebarSections/SidebarSections'
import type { ChartPreset } from 'features/charts-v2/types/chart-types'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'

export type ControlsSidebarBuiltInSection =
  | 'chart'
  | 'metric'
  | 'breakdown'
  | 'filter'

export interface ControlsSidebarCustomSection {
  id: string
  content: React.ReactNode
}

export type ControlsSidebarSection =
  | ControlsSidebarBuiltInSection
  | ControlsSidebarCustomSection

export interface ChartSectionProps {
  /** Which picker sections to show, in render order. `/charts` passes
   *  `['templates', 'freeform']`; a dashboard tile appends its "Widgets:"
   *  section so a tile can convert between chart and widget in place. */
  sections: readonly ChartTypePickerSectionSpec[]
  /** Absent for a tile with no chart at all (a dashboard widget/text tile). */
  selectedChartType?: ChartTypeV2
  selectedPresetId?: string | null
  onChartTypeChange: (chartType: ChartTypeV2) => void
  onPresetSelect?: (preset: ChartPreset | null) => void
  /** What the trigger shows in place of a resolved chart preset — see
   *  `ChartTypeSelectorSelectionOverride`. Absent for every chart caller. */
  selectionOverride?: ChartTypeSelectorSelectionOverride
}

export interface ControlsSidebarHeaderProps {
  isCollapsed: boolean
  onToggleCollapse: () => void
  collapseTooltip?: React.ReactNode
  expandTooltip?: React.ReactNode
  title: string
  help?: React.ReactNode
  syncToggle?: React.ReactNode
}

export interface ControlsSidebarPanelSwitcherProps {
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}

export interface ControlsSidebarProps {
  sections: readonly ControlsSidebarSection[]
  header: ControlsSidebarHeaderProps
  panelSwitcher?: ControlsSidebarPanelSwitcherProps
  chart?: ChartSectionProps
  metric?: MetricControlProps
  breakdown?: BreakdownControlProps
  filter?: FilterControlProps
}

export const ControlsSidebar: React.FC<ControlsSidebarProps> = ({
  sections,
  header,
  panelSwitcher,
  chart,
  metric,
  breakdown,
  filter,
}) => {
  const liftFilterHeaderActions =
    sections.length === 1 && sections[0] === 'filter'
  const [filterHeaderSlot, setFilterHeaderSlot] =
    useState<HTMLDivElement | null>(null)

  assertSectionsSatisfied(sections, { chart, metric, breakdown, filter })

  const sectionProps = {
    metric,
    breakdown,
    filter,
    liftFilterHeaderActions,
    filterHeaderSlot,
  }

  if (header.isCollapsed) {
    return (
      <CollapsedShell
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('a, button, [role="button"]'))
            return
          header.onToggleCollapse()
        }}
      >
        <OpenSidebarButton
          onClick={header.onToggleCollapse}
          size={30}
          title={header.expandTooltip}
        />
      </CollapsedShell>
    )
  }

  return (
    <Shell>
      {panelSwitcher && (
        <PanelSwitcherRow>
          <PressableToggleButtonGroup<string>
            value={panelSwitcher.value}
            onChange={panelSwitcher.onChange}
            options={panelSwitcher.options}
            block
            colors={PANEL_SWITCHER_COLORS}
          />
        </PanelSwitcherRow>
      )}
      <ScrollArea>
        <SectionsColumn>
          <ChartSectionWrapper>
            <HeaderRow>
              <HeaderTitleGroup>
                <HeaderTitle>{header.title}</HeaderTitle>
                {header.help}
              </HeaderTitleGroup>
              <HeaderActions>
                {header.syncToggle}
                {liftFilterHeaderActions && (
                  <FilterHeaderActionsSlot ref={setFilterHeaderSlot} />
                )}
                <CloseSidebarButton
                  onClick={header.onToggleCollapse}
                  size={30}
                  title={header.collapseTooltip}
                />
              </HeaderActions>
            </HeaderRow>
            {sections.includes('chart') && chart && (
              <ChartTypeSelector
                sections={chart.sections}
                selectedChartType={chart.selectedChartType}
                selectedPresetId={chart.selectedPresetId}
                onChartTypeChange={chart.onChartTypeChange}
                onPresetSelect={chart.onPresetSelect}
                selectionOverride={chart.selectionOverride}
              />
            )}
            <SidebarSections
              sections={sections.slice(0, 1)}
              {...sectionProps}
            />
          </ChartSectionWrapper>

          <SidebarSections sections={sections.slice(1)} {...sectionProps} />
        </SectionsColumn>
      </ScrollArea>
    </Shell>
  )
}
